import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { useEmployeeAuth } from "../../context/EmployeeAuthContext";

// Forced (first-login / post-reset) or voluntary PIN change. Mobile numpad UX.
export default function EmpChangePin() {
  const { employee, changePin, logout } = useEmployeeAuth();
  const navigate = useNavigate();
  const forced = !!employee?.pin_must_change;

  const [step, setStep] = useState(forced ? "new" : "current"); // current -> new -> confirm
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const active = step === "current" ? currentPin : step === "new" ? newPin : confirmPin;
  const setActive = step === "current" ? setCurrentPin : step === "new" ? setNewPin : setConfirmPin;

  const pad = (digit) => {
    setErr("");
    if (digit === "del") setActive((p) => p.slice(0, -1));
    else if (active.length < 8) setActive((p) => p + digit);
  };

  const next = async () => {
    setErr("");
    if (step === "current") {
      if (currentPin.length < 4) return setErr("Masukkan PIN lama");
      setStep("new"); return;
    }
    if (step === "new") {
      if (newPin.length < 4) return setErr("PIN baru minimal 4 digit");
      setStep("confirm"); return;
    }
    // confirm
    if (confirmPin !== newPin) { setErr("PIN tidak cocok"); setConfirmPin(""); return; }
    setLoading(true);
    const res = await changePin(newPin, forced ? undefined : currentPin);
    setLoading(false);
    if (res.ok) navigate("/karyawan", { replace: true });
    else { setErr(res.error); setStep(forced ? "new" : "current"); setNewPin(""); setConfirmPin(""); }
  };

  const TITLE = { current: "PIN Lama", new: "PIN Baru", confirm: "Konfirmasi PIN Baru" };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950 px-5 py-8 text-white" data-testid="emp-change-pin-page">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-orange-500/30">
            <ShieldCheck size={30} className="text-slate-900" />
          </div>
          <h1 className="font-heading text-2xl font-bold">{forced ? "Atur PIN Baru" : "Ganti PIN"}</h1>
          <p className="mt-1 text-sm text-slate-400">
            {forced ? "Demi keamanan, ganti PIN awal Anda sebelum melanjutkan." : "Perbarui PIN Anda."}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">{TITLE[step]}</label>
            <div className="w-full rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-3 text-center text-2xl tracking-[0.5em] text-white" data-testid="emp-change-pin-display">
              {active.replace(/./g, "•") || <span className="text-slate-600">••••</span>}
            </div>
          </div>

          {err && <p className="rounded-lg bg-red-500/15 px-3 py-2 text-center text-sm text-red-300" data-testid="emp-change-pin-error">{err}</p>}

          <div className="grid grid-cols-3 gap-2.5">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "del"].map((d, i) => (
              d === "" ? <div key={i} /> : (
                <button type="button" key={i} onClick={() => pad(d)} data-testid={`emp-change-pin-key-${d}`}
                  className="rounded-xl bg-slate-800/60 py-3 text-lg font-semibold text-white transition-colors hover:bg-slate-700 active:scale-95">
                  {d === "del" ? "⌫" : d}
                </button>
              )
            ))}
          </div>

          <button onClick={next} disabled={loading || active.length < 4} data-testid="emp-change-pin-next"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-3.5 font-bold text-slate-900 transition-all hover:shadow-lg hover:shadow-orange-500/30 disabled:opacity-40">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <>{step === "confirm" ? "Simpan PIN" : "Lanjut"} <ArrowRight size={18} /></>}
          </button>

          <button onClick={forced ? logout : () => navigate("/karyawan/profil")} data-testid="emp-change-pin-cancel"
            className="w-full py-2 text-center text-sm text-slate-400">
            {forced ? "Keluar" : "Batal"}
          </button>
        </div>
      </div>
    </div>
  );
}
